/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { TourState } from '@/components/Tours/types';

// Referenced as a literal (not imported) to keep this slice free of the user-actions
// import chain. Matches userSlice name 'user' + reducer 'unsetUserData'.
const UNSET_USER_DATA = 'user/unsetUserData';

/** Cap the client-only history stack so persisted state can't grow unbounded. */
export const STEP_HISTORY_CAP = 100;

export interface ToursState {
  tours: Record<string, TourState>;
}

const initialState: ToursState = {
  tours: {},
};

const emptyTour = (): TourState => ({
  stepHistory: [],
  completedTimes: 0,
});

/** Get-or-init the per-tour state so reducers can mutate it directly. */
const ensure = (state: ToursState, tourId: string): TourState => {
  if (!state.tours[tourId]) state.tours[tourId] = emptyTour();
  return state.tours[tourId];
};

const capHistory = (history: string[]): string[] =>
  history.length > STEP_HISTORY_CAP ? history.slice(history.length - STEP_HISTORY_CAP) : history;

/** Mirror the head of the history onto the analytics field that travels to the server. */
const syncCurrentStepId = (tour: TourState): void => {
  tour.currentStepId = tour.stepHistory[tour.stepHistory.length - 1];
};

type TourAction<Extra = {}> = PayloadAction<{ tourId: string } & Extra>;

const toursSlice = createSlice({
  name: 'tours',
  initialState,
  reducers: {
    startTour: (state, { payload }: TourAction<{ firstStepId: string }>) => {
      const tour = ensure(state, payload.tourId);
      const now = new Date().toISOString();
      tour.stepHistory = [payload.firstStepId];
      tour.skippedAt = undefined;
      tour.completedAt = undefined;
      tour.startedAt = now;
      tour.furthestStepId = undefined;
      tour.furthestStepNumber = undefined;
      syncCurrentStepId(tour);
    },
    /**
     * Restart = a fresh run. `startedAt`/`completedAt` describe the CURRENT run and are reset;
     * `lastStartedAt`/`lastCompletedAt` retain the previous run's values as history, and
     * `completedTimes` keeps accumulating across runs.
     */
    restartTour: (state, { payload }: TourAction<{ firstStepId: string }>) => {
      const tour = ensure(state, payload.tourId);
      const now = new Date().toISOString();
      tour.lastStartedAt = tour.startedAt ?? tour.lastStartedAt;
      if (tour.completedAt) tour.lastCompletedAt = tour.completedAt;
      tour.startedAt = now;
      tour.completedAt = undefined;
      tour.skippedAt = undefined;
      tour.stepHistory = [payload.firstStepId];
      tour.furthestStepId = undefined;
      tour.furthestStepNumber = undefined;
      syncCurrentStepId(tour);
    },
    goToStep: (state, { payload }: TourAction<{ stepId: string; stepNumber?: number }>) => {
      const tour = ensure(state, payload.tourId);
      tour.stepHistory = capHistory([...tour.stepHistory, payload.stepId]);
      syncCurrentStepId(tour);
      // Monotonic: going back and forward again never lowers the mark.
      if (payload.stepNumber !== undefined && payload.stepNumber > (tour.furthestStepNumber ?? -1)) {
        tour.furthestStepNumber = payload.stepNumber;
        tour.furthestStepId = payload.stepId;
      }
    },
    /**
     * Generic "go back": pop `steps` entries (default 1). The only mechanism a button can
     * trigger that a pre-authored targetStepId cannot express (merge points have many possible
     * parents). The caller decides the distance — see goBackDistance, which skips over steps
     * that cannot be re-entered — so this stays free of any config knowledge.
     */
    goBack: (state, { payload }: TourAction<{ steps?: number }>) => {
      const tour = state.tours[payload.tourId];
      if (!tour) return;
      // Never pop the first entry: a tour always stands on some step.
      const remaining = Math.max(1, tour.stepHistory.length - Math.max(1, payload.steps ?? 1));
      if (remaining >= tour.stepHistory.length) return;
      tour.stepHistory = tour.stepHistory.slice(0, remaining);
      syncCurrentStepId(tour);
    },
    // Rewind in one update (undo stack): drop everything after the target's last occurrence.
    goBackTo: (state, { payload }: TourAction<{ stepId: string }>) => {
      const tour = state.tours[payload.tourId];
      if (!tour) return;
      const idx = tour.stepHistory.lastIndexOf(payload.stepId);
      if (idx < 0 || idx === tour.stepHistory.length - 1) return;
      tour.stepHistory = tour.stepHistory.slice(0, idx + 1);
      syncCurrentStepId(tour);
    },
    skipTour: (state, { payload }: TourAction) => {
      const tour = ensure(state, payload.tourId);
      tour.skippedAt = new Date().toISOString();
    },
    completeTour: (state, { payload }: TourAction) => {
      const tour = ensure(state, payload.tourId);
      if (tour.completedAt) return; // already terminal; don't double-count
      tour.completedAt = new Date().toISOString();
      tour.lastCompletedAt = tour.completedAt;
      tour.completedTimes += 1;
    },
    /** One-time seed of a full per-tour state (used by the legacy guidedTour migration). */
    seedTourState: (state, { payload }: PayloadAction<{ tourId: string; tourState: TourState }>) => {
      state.tours[payload.tourId] = payload.tourState;
      syncCurrentStepId(state.tours[payload.tourId]);
    },
  },
  extraReducers: (builder) => {
    // Clear all client tour progress on logout so the next user starts clean.
    builder.addCase(UNSET_USER_DATA, () => initialState);
  },
});

export default toursSlice;
